// src/api/messages_api.js
import axios from 'axios';
import { getHeader } from '../types.js';

/* Success envelope: { data }. Error envelope: { error: "<message>" } + HTTP status. */
const unwrap = (res) => {
  const body = res && res.data;
  return body && typeof body === 'object' && 'data' in body ? body.data : body;
};

const messageFromBody = (body) => {
  if (!body) return 'Request failed';
  if (typeof body.error === 'string') return body.error;
  if (body.error && body.error.message) return body.error.message;
  if (typeof body.message === 'string') return body.message;
  return 'Request failed';
};

const fail = (error) =>
  Promise.reject(
    error && error.response
      ? { status: error.response.status, message: messageFromBody(error.response.data) }
      : { status: 0, message: 'Network error. Please try again.' },
  );

export const messagesApi = {
  listConversations: ({ cursor, limit = 20, search, clientIds } = {}) =>
    axios
      .get('/api/chat/conversations', {
        headers: getHeader(),
        params: { cursor, limit, ...(search ? { search } : {}), ...(clientIds ? { clientIds } : {}) },
      })
      .then(unwrap)
      .catch(fail),

  unreadCount: () =>
    axios.get('/api/chat/unread-count', { headers: getHeader() }).then(unwrap).catch(fail),

  openConversation: (jobId) =>
    axios
      .get(`/api/chat/conversations/${encodeURIComponent(jobId)}`, { headers: getHeader() })
      .then(unwrap)
      .catch(fail),

  listMessages: (jobId, { before, limit = 30 } = {}) =>
    axios
      .get(`/api/chat/conversations/${encodeURIComponent(jobId)}/messages`, {
        headers: getHeader(),
        params: { before, limit },
      })
      .then(unwrap)
      .catch(fail),

  listReplies: (messageId, { before, limit = 30 } = {}) =>
    axios
      .get(`/api/chat/messages/${encodeURIComponent(messageId)}/replies`, {
        headers: getHeader(),
        params: { before, limit },
      })
      .then(unwrap)
      .catch(fail),

  sendMessage: (jobId, { body, parentMessageId, clientMessageId }) =>
    axios
      .post(
        `/api/chat/conversations/${encodeURIComponent(jobId)}/messages`,
        { body, parentMessageId, clientMessageId },
        { headers: getHeader() },
      )
      .then(unwrap)
      .catch(fail),

  markRead: (jobId, messageId) =>
    axios
      .post(
        `/api/chat/conversations/${encodeURIComponent(jobId)}/read`,
        { messageId },
        { headers: getHeader() },
      )
      .then(unwrap)
      .catch(fail),

  availableJobs: ({ clientId, search } = {}) =>
    axios
      .get('/api/chat/available-jobs', {
        headers: getHeader(),
        params: { clientId, ...(search ? { search } : {}) },
      })
      .then(unwrap)
      .catch(fail),

  /* Admin: create a conversation, optionally seeding a first message */
  createConversation: (jobId, seedMessage) =>
    axios
      .post('/api/chat/conversations', { jobId, seedMessage }, { headers: getHeader() })
      .then(unwrap)
      .catch(fail),

  /* Reactions */
  listReactions: () =>
    axios.get('/api/chat/reactions', { headers: getHeader() }).then(unwrap).catch(fail),

  toggleReaction: (messageId, emoji) =>
    axios
      .post(
        `/api/chat/messages/${encodeURIComponent(messageId)}/reactions`,
        { emoji },
        { headers: getHeader() },
      )
      .then(unwrap)
      .catch(fail),
};

export default messagesApi;
